import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  MapPin, Clock, Building2, DollarSign, Calendar, ArrowLeft,
  Bookmark, BookmarkCheck, CheckCircle2, TrendingUp, Sparkles,
  Briefcase, Award, Share2, User, Hash, WifiOff, RefreshCw,
} from "lucide-react";
import { JobPosting } from "@/services/api/types";
import { JobDescriptionContent } from "@/components/jobs/JobDescriptionContent";
import { useToast } from "@/app/hooks/use-toast";
import { AnimateOnScroll } from "@/components/common/AnimateOnScroll";
import { JobDetailSkeleton } from "@/components/jobs/JobDetailSkeleton";
import DcPage from "@/components/landing/dcv2/DcPage";
import { useAuth } from "@/app/hooks/useAuth";
import { jobsApi } from "@/services/endpoints/jobs";
import { formatDate } from "@/utils/formatters/date.formatters";
import { glassCardStyle, surfaceStyle } from "@/components/auth/authStyles";
import { useSavedJobs } from "@/app/hooks/useSavedJobs";

const employmentLabel = (t: string) =>
  ({ full_time: "Full Time", part_time: "Part Time", contract: "Contract", internship: "Internship", "Full-time": "Full Time", "Part-time": "Part Time", Contract: "Contract", Internship: "Internship" })[t] || t;

const workplaceLabel = (t: string) =>
  ({ remote: "Remote", on_site: "On Site", hybrid: "Hybrid", Remote: "Remote", "On-site": "On Site", Hybrid: "Hybrid" })[t] || t;

const seniorityLabel = (l: string) =>
  ({ entry: "Entry Level", entry_level: "Entry Level", mid: "Mid Level", mid_level: "Mid Level", senior: "Senior Level", senior_level: "Senior Level", lead: "Lead", director: "Director", executive: "Executive", Entry: "Entry Level", Mid: "Mid Level", Senior: "Senior Level", Lead: "Lead", Director: "Director", Executive: "Executive" })[l] || l;

const formatPostingDate = (d: string) => formatDate(d, d, "MMMM d, yyyy");

/**
 * Formats a date-only deadline ("YYYY-MM-DD") without timezone drift. Parsing
 * such a string via `new Date()` would treat it as UTC midnight and can render
 * the previous day in negative-offset zones, so build a local date instead.
 */
const formatDeadline = (ymd: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return formatPostingDate(ymd);
  const local = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return local.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

/** Grid + glow atmosphere shared by every state of this page. */
function PageBackdrop() {
  return (
    <>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(var(--landing-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--landing-grid-line) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 70% 50% at 50% 20%, black, transparent)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 50% at 50% 20%, black, transparent)",
        }}
      />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(var(--acc2-rgb),0.12) 0%, rgba(var(--acc2-rgb),0.06) 45%, transparent 70%)",
          filter: "blur(80px)",
          opacity: "var(--landing-glow-mult)",
        }}
      />
    </>
  );
}

/** Electric meta pill used in the title card. */
function MetaPill({ icon: Icon, children, tone = "surface" }: { icon: React.ElementType; children: React.ReactNode; tone?: "surface" | "cyan" | "purple" | "teal" }) {
  const tones: Record<string, React.CSSProperties> = {
    surface: { background: "var(--landing-surface)", color: "var(--landing-text-secondary)", border: "1px solid var(--landing-glass-border)" },
    cyan: { background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.18)" },
    purple: { background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.18)" },
    teal: { background: "rgba(var(--pos-rgb),0.08)", color: "var(--pos)", border: "1px solid rgba(var(--pos-rgb),0.22)" },
  };
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium"
      style={tones[tone]}
    >
      <Icon className="h-3 w-3" />
      {children}
    </span>
  );
}

export default function JobDetails() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, user } = useAuth();
  const { isSaved, toggleSave } = useSavedJobs();

  const [job, setJob] = useState<JobPosting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const saved = job ? isSaved(job.id) : false;

  useEffect(() => {
    if (jobId) fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      setUnavailable(false);
      const data = await jobsApi.getById(jobId!);
      setJob(data.data);
    } catch (err) {
      // A 404 means the posting expired/was removed — show a dedicated state,
      // not the generic connection error.
      if ((err as { code?: string })?.code === "JOB_UNAVAILABLE") {
        setUnavailable(true);
        return;
      }
      const msg = err instanceof Error ? err.message : "Failed to load job details.";
      setError(msg);
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Phones get the system share sheet; desktops copy the link (the Windows share
  // sheet is easy to miss), with the link itself in the toast if copying fails.
  const handleShare = async () => {
    if (!job) return;
    const url = `${window.location.origin}/job/${job.id}`;
    const title = job.organizationName ? `${job.title} at ${job.organizationName}` : job.title;
    const touch = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
    if (touch && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        if ((err as { name?: string })?.name === "AbortError") return; // the user closed the sheet
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied", description: "Paste it anywhere to share this job." });
    } catch {
      toast({ title: "Copy this link to share the job", description: url });
    }
  };

  const handleApplyNow = () => {
    if (!job) return;
    if (job.candidateApplicationContext?.hasApplied) {
      toast({
        title: "Already applied",
        description: "You've already applied to this position. Track it under Profile › Applications.",
      });
      return;
    }
    if (isAuthenticated) {
      navigate(`/apply/${job.id}`);
    } else {
      navigate(`/auth?redirect=${encodeURIComponent(`/apply/${job.id}`)}`);
    }
  };

  const handleSaveJob = async () => {
    if (!job) return;
    if (!isAuthenticated || !user?.id) {
      navigate(`/auth?redirect=${encodeURIComponent(`/job/${job.id}`)}`);
      return;
    }
    try {
      const nowSaved = await toggleSave(job.id);
      toast({
        title: nowSaved ? "Job saved" : "Job removed",
        description: nowSaved
          ? "Find it later under Profile › Saved jobs."
          : "This job has been removed from your saved jobs.",
      });
    } catch {
      toast({
        title: "Couldn't update saved jobs",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <DcPage>
        <JobDetailSkeleton />
      </DcPage>
    );
  }

  if (unavailable) {
    return (
      <DcPage>
        <section
          className="relative min-h-[70vh] flex items-center justify-center overflow-hidden px-6"
          style={{ background: "var(--landing-bg)" }}
        >
          <PageBackdrop />
          <div className="relative z-10 text-center max-w-lg mx-auto">
            <div className="relative mx-auto w-24 h-24 mb-8">
              <div className="absolute inset-0 rounded-3xl rotate-6" style={{ background: "rgba(var(--acc2-rgb),0.08)" }} />
              <div
                className="relative w-full h-full rounded-3xl flex items-center justify-center"
                style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)" }}
              >
                <Clock className="h-10 w-10 text-[var(--acc2)]/70" />
              </div>
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] mb-3 text-[var(--acc2)]">
              No longer available
            </p>
            <h1 className="text-2xl font-bold tracking-tight mb-3" style={{ color: "var(--landing-text)" }}>
              This position has closed
            </h1>
            <p className="text-sm leading-relaxed max-w-sm mx-auto mb-8" style={{ color: "var(--landing-text-muted)" }}>
              This posting is no longer accepting applications. Browse our open roles to find your next opportunity.
            </p>
            <Link to="/jobs" className="btn-electric px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Browse open roles
            </Link>
          </div>
        </section>
      </DcPage>
    );
  }

  if (error || !job) {
    return (
      <DcPage>
        <section
          className="relative min-h-[70vh] flex items-center justify-center overflow-hidden px-6"
          style={{ background: "var(--landing-bg)" }}
        >
          <PageBackdrop />
          <div className="relative z-10 text-center max-w-lg mx-auto">
            <div className="relative mx-auto w-24 h-24 mb-8">
              <div className="absolute inset-0 rounded-3xl rotate-6" style={{ background: "rgba(var(--acc2-rgb),0.08)" }} />
              <div
                className="relative w-full h-full rounded-3xl flex items-center justify-center"
                style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)" }}
              >
                {error ? (
                  <WifiOff className="h-10 w-10 text-[var(--acc2)]/70" />
                ) : (
                  <Briefcase className="h-10 w-10 text-[var(--acc2)]/70" />
                )}
              </div>
            </div>
            <h1 className="text-2xl font-bold tracking-tight mb-3" style={{ color: "var(--landing-text)" }}>
              {error ? "Unable to load this position" : "Position not found"}
            </h1>
            <p className="text-sm leading-relaxed max-w-sm mx-auto mb-8" style={{ color: "var(--landing-text-muted)" }}>
              {error
                ? "We couldn't connect to the server right now. This is usually temporary. Check your connection and try again."
                : "The position you're looking for doesn't exist or may have been removed."}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {error && (
                <button onClick={fetchJobDetails} className="btn-electric px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
              )}
              <Link
                to="/jobs"
                className="px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2 transition-colors"
                style={surfaceStyle}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Jobs
              </Link>
            </div>
          </div>
        </section>
      </DcPage>
    );
  }

  const summaryItems = [
    { icon: Calendar, label: "Posted", value: formatPostingDate(job.createdAt) },
    ...(job.applicationDeadline ? [{ icon: Clock, label: "Deadline", value: formatDeadline(job.applicationDeadline) }] : []),
    { icon: MapPin, label: "Location", value: job.location },
    ...(job.salaryRange ? [{ icon: DollarSign, label: "Salary", value: job.salaryRange }] : []),
    { icon: Clock, label: "Type", value: employmentLabel(job.employmentType) },
    { icon: Building2, label: "Workplace", value: workplaceLabel(job.workplaceType) },
    { icon: TrendingUp, label: "Level", value: seniorityLabel(job.seniorityLevel) },
  ];

  const accordionSections = [
    { value: "description", icon: Briefcase, label: "Job Description", content: job.description },
    ...(job.qualifications ? [{ value: "qualifications", icon: Award, label: "Qualifications", content: job.qualifications }] : []),
    ...(job.perks ? [{ value: "perks", icon: Sparkles, label: "Perks & Benefits", content: job.perks }] : []),
  ];

  return (
    <DcPage>
      <div className="relative min-h-screen overflow-hidden" style={{ background: "var(--landing-bg)" }}>
        <PageBackdrop />

        <div className="relative z-10">
          <section className="pt-20 md:pt-24 pb-4">
            <div className="container">
              <div className="mx-auto max-w-5xl">
                <Link
                  to="/jobs"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors hover:text-[var(--acc2)] mb-6"
                  style={{ color: "var(--landing-text-muted)" }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to Jobs
                </Link>
              </div>
            </div>
          </section>

          <section className="pb-16 md:pb-20 lg:pb-24">
            <div className="container">
              <div className="mx-auto max-w-5xl">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                  <div className="space-y-5">
                    <AnimateOnScroll>
                      <div className="rounded-2xl overflow-hidden" style={glassCardStyle}>
                        <div
                          className="h-[3px] w-full"
                          style={{ background: "rgba(var(--acc2-rgb),.5)" }}
                        />
                        <div className="p-6">
                          <div className="flex items-center gap-2.5 mb-3">
                            <Building2 className="h-4 w-4" style={{ color: "var(--landing-text-dim)" }} />
                            <span className="text-sm font-medium" style={{ color: "var(--landing-text-muted)" }}>
                              {job.organizationName}
                            </span>
                          </div>
                          <h1 className="text-3xl font-bold tracking-tight mb-4 [overflow-wrap:anywhere]" style={{ color: "var(--landing-text)" }}>
                            {job.title}
                          </h1>
                          <div className="flex flex-wrap items-center gap-2.5">
                            {job.salaryRange && (
                              <span
                                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold"
                                style={{ background: "rgba(var(--pos-rgb),0.08)", color: "var(--pos)", border: "1px solid rgba(var(--pos-rgb),0.22)" }}
                              >
                                <DollarSign className="h-3.5 w-3.5" />
                                {job.salaryRange}
                              </span>
                            )}
                            <MetaPill icon={Clock}>{employmentLabel(job.employmentType)}</MetaPill>
                            <MetaPill icon={MapPin}>{job.location}</MetaPill>
                            <MetaPill icon={Building2} tone="cyan">{workplaceLabel(job.workplaceType)}</MetaPill>
                            <MetaPill icon={TrendingUp} tone="purple">{seniorityLabel(job.seniorityLevel)}</MetaPill>
                          </div>
                        </div>
                      </div>
                    </AnimateOnScroll>

                    <AnimateOnScroll delay={50}>
                      <div className="rounded-2xl overflow-hidden" style={glassCardStyle}>
                        <Accordion
                          type="multiple"
                          defaultValue={accordionSections.map((s) => s.value)}
                          className="w-full"
                        >
                          {accordionSections.map((section, i) => (
                            <AccordionItem
                              key={section.value}
                              value={section.value}
                              className="border-0"
                              style={i === 0 ? undefined : { borderTop: "1px solid var(--landing-glass-border)" }}
                            >
                              <AccordionTrigger className="px-6 py-4 hover:no-underline rounded-md">
                                <div className="flex items-center gap-2.5">
                                  <section.icon className="h-4 w-4 text-[var(--acc2)]" />
                                  <span className="text-sm font-semibold" style={{ color: "var(--landing-text)" }}>
                                    {section.label}
                                  </span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="px-6 pb-6 pt-3">
                                <JobDescriptionContent content={section.content} className="text-sm leading-relaxed" />
                              </AccordionContent>
                            </AccordionItem>
                          ))}

                          {job.applicationDeadline && (
                            <AccordionItem value="deadline" className="border-0" style={{ borderTop: "1px solid var(--landing-glass-border)" }}>
                              <AccordionTrigger className="px-6 py-4 hover:no-underline rounded-md">
                                <div className="flex items-center gap-2.5">
                                  <Calendar className="h-4 w-4 text-[var(--acc2)]" />
                                  <span className="text-sm font-semibold" style={{ color: "var(--landing-text)" }}>
                                    Application Deadline
                                  </span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="px-6 pb-6 pt-3">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-[var(--acc2)]" />
                                  <p className="text-sm font-semibold" style={{ color: "var(--landing-text)" }}>
                                    {formatDeadline(job.applicationDeadline)}
                                  </p>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          )}
                        </Accordion>
                      </div>
                    </AnimateOnScroll>
                  </div>

                  <div>
                    <div className="lg:sticky lg:top-24 space-y-5">
                      <AnimateOnScroll delay={80} variant="fade-left">
                        <div className="rounded-2xl p-5 space-y-3" style={glassCardStyle}>
                          {isAuthenticated ? (
                            <>
                              <div
                                className="flex items-center gap-2.5 p-2.5 rounded-lg"
                                style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)" }}
                              >
                                <div
                                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ background: "rgba(var(--acc2-rgb),0.1)", border: "1px solid rgba(var(--acc2-rgb),0.2)" }}
                                >
                                  <User className="h-4 w-4 text-[var(--acc2)]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium mb-0.5" style={{ color: "var(--landing-text-muted)" }}>Signed in as</p>
                                  <p className="text-xs font-semibold truncate" style={{ color: "var(--landing-text)" }}>{user?.email}</p>
                                </div>
                              </div>
                              {job.candidateApplicationContext?.hasApplied ? (
                                <div
                                  className="w-full px-6 py-3 rounded-lg font-semibold inline-flex items-center justify-center gap-2"
                                  style={{ background: "rgba(var(--pos-rgb),0.08)", color: "var(--pos)", border: "1px solid rgba(var(--pos-rgb),0.22)" }}
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                  Already applied
                                </div>
                              ) : (
                                <button onClick={handleApplyNow} className="btn-electric w-full px-6 py-3 rounded-lg font-semibold inline-flex items-center justify-center gap-2">
                                  <CheckCircle2 className="h-4 w-4" />
                                  Apply Now
                                </button>
                              )}
                            </>
                          ) : (
                            <button onClick={handleApplyNow} className="btn-electric w-full px-6 py-3 rounded-lg font-semibold inline-flex items-center justify-center gap-2">
                              Sign In to Apply
                              <ArrowLeft className="h-4 w-4 rotate-180" />
                            </button>
                          )}

                          <div className="flex gap-2">
                            <button
                              onClick={handleSaveJob}
                              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
                              style={
                                saved
                                  ? { background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.25)" }
                                  : surfaceStyle
                              }
                            >
                              {saved ? (
                                <><BookmarkCheck className="h-3.5 w-3.5" />Saved</>
                              ) : (
                                <><Bookmark className="h-3.5 w-3.5" />Save</>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={handleShare}
                              className="px-3 py-2.5 rounded-lg inline-flex items-center justify-center transition-colors"
                              style={surfaceStyle}
                              aria-label="Share this job"
                              title="Share this job"
                            >
                              <Share2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </AnimateOnScroll>

                      <AnimateOnScroll delay={120} variant="fade-left">
                        <div className="rounded-2xl p-5" style={glassCardStyle}>
                          <h2 className="text-sm font-semibold mb-3" style={{ color: "var(--landing-text)" }}>
                            Job Summary
                          </h2>
                          <div className="space-y-0">
                            {summaryItems.map((item) => (
                              <div
                                key={item.label}
                                className="flex items-start gap-2.5 py-2.5"
                                style={{ borderBottom: "1px solid var(--landing-glass-border)" }}
                              >
                                <item.icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--landing-text-dim)" }} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium mb-1" style={{ color: "var(--landing-text-muted)" }}>{item.label}</p>
                                  <p className="text-xs font-semibold" style={{ color: "var(--landing-text)" }}>{item.value}</p>
                                </div>
                              </div>
                            ))}
                            <div className="flex items-start gap-2.5 pt-2.5">
                              <Hash className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--landing-text-dim)" }} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium mb-1" style={{ color: "var(--landing-text-muted)" }}>Job ID</p>
                                <p className="text-xs font-mono" style={{ color: "var(--landing-text-dim)" }}>{job.jobId}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </AnimateOnScroll>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </DcPage>
  );
}
